import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Konverter Gram ke Cangkir - Konversi Tepat Bahan Masakan dan Kue";
const DESCRIPTION = "Konverter cepat dan akurat untuk mengonversi gram ke cangkir dan sebaliknya. Ideal untuk tepung, gula, mentega, dan cairan. Masukkan jumlah dalam gram dan ketahui berapa cangkir yang Anda butuhkan.";
const KEYWORDS = "konversi gram ke cangkir, konverter takaran, hitung cangkir tepung dalam gram, konversi satuan memasak, grams to cups converter, cooking measurement converter";
const OG_URL = "https://do-calculate.com/calculator/id/grams-to-cups/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Konverter Gram ke Cangkir",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Konverter Gram ke Cangkir",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa cangkir untuk 250 gram tepung?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "250 gram tepung terigu biasa setara dengan sekitar 2 cangkir (2 cup) berdasarkan ukuran cangkir standar 240 ml. Tepung beratnya sekitar 125 gram per cangkir.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara mengonversi gula dari gram ke cangkir?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Satu cangkir gula putih biasa beratnya sekitar 200 gram. Jadi 100 gram gula setara dengan setengah cangkir. Gula merah sedikit lebih berat karena kelembapannya, satu cangkir gula merah padat beratnya 220 gram.",
        },
      },
      {
        "@type": "Question",
        name: "Apakah konversi gram ke cangkir berbeda antara tepung dan gula?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ya, sangat berbeda karena kepadatannya berbeda. Satu cangkir tepung jauh lebih ringan (125 gram) dari satu cangkir gula (200 gram). Konverter ini menggunakan kepadatan spesifik untuk setiap bahan guna memastikan akurasi konversi.",
        },
      },
    ],
  },
];

// Ingredient densities (grams per cup - based on US cup 240ml)
const INGREDIENT_DENSITIES = {
  flour: { name: "Tepung terigu (biasa)", gramsPerCup: 125 },
  breadFlour: { name: "Tepung roti", gramsPerCup: 130 },
  wholeWheatFlour: { name: "Tepung gandum utuh", gramsPerCup: 120 },
  sugar: { name: "Gula putih", gramsPerCup: 200 },
  brownSugar: { name: "Gula merah (padat)", gramsPerCup: 220 },
  powderedSugar: { name: "Gula bubuk", gramsPerCup: 120 },
  butter: { name: "Mentega", gramsPerCup: 227 },
  milk: { name: "Susu cair", gramsPerCup: 240 },
  water: { name: "Air", gramsPerCup: 240 },
  oil: { name: "Minyak sayur", gramsPerCup: 218 },
  honey: { name: "Madu", gramsPerCup: 340 },
  rice: { name: "Beras (mentah)", gramsPerCup: 190 },
  oats: { name: "Oat/Gandum", gramsPerCup: 90 },
  cocoa: { name: "Kakao", gramsPerCup: 100 },
};

function GramsToCupsCalculator() {
  const [ingredient, setIngredient] = useState("flour");
  const [customDensity, setCustomDensity] = useState("");
  const [grams, setGrams] = useState("");
  const [cups, setCups] = useState("");
  const [unit, setUnit] = useState("grams");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateFromGrams = () => {
    try {
      if (!grams || parseFloat(grams) <= 0) {
        setError("");
        setResult(null);
        return;
      }
      const gramsValue = parseFloat(grams);
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("Masukkan kepadatan yang valid (gram/cangkir)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }
      const cupsValue = gramsValue / gramsPerCup;
      setCups(cupsValue.toFixed(3));
      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "Bahan kustom" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("Kesalahan perhitungan");
      setResult(null);
    }
  };

  const calculateFromCups = () => {
    try {
      if (!cups || parseFloat(cups) <= 0) {
        setError("");
        setResult(null);
        return;
      }
      const cupsValue = parseFloat(cups);
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("Masukkan kepadatan yang valid (gram/cangkir)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }
      const gramsValue = cupsValue * gramsPerCup;
      setGrams(gramsValue.toFixed(1));
      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "Bahan kustom" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("Kesalahan perhitungan");
      setResult(null);
    }
  };

  const handleGramsChange = (e) => {
    const value = e.target.value;
    setGrams(value);
    setUnit("grams");
    if (value) { calculateFromGrams(); } else { setResult(null); }
  };

  const handleCupsChange = (e) => {
    const value = e.target.value;
    setCups(value);
    setUnit("cups");
    if (value) { calculateFromCups(); } else { setResult(null); }
  };

  useEffect(() => {
    if (unit === "grams" && grams) { calculateFromGrams(); }
    else if (unit === "cups" && cups) { calculateFromCups(); }
  }, [ingredient, customDensity]);

  const handleClear = () => {
    setGrams("");
    setCups("");
    setUnit("grams");
    setResult(null);
    setError("");
    setCustomDensity("");
  };

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Konverter Gram ke Cangkir</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Konverter Gram ke Cangkir</strong> adalah alat cerdas untuk mengonversi takaran dalam resep. Kita semua menghadapi masalah mengonversi gram ke cangkir, terutama untuk bahan yang berbeda. Konverter ini menghitung jumlah dengan tepat sesuai jenis bahan.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Konversi Mudah antara Gram dan Cangkir</h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="ingredient">Jenis Bahan</label>
                  <select
                    id="ingredient"
                    className="form-select"
                    value={ingredient}
                    onChange={(e) => setIngredient(e.target.value)}
                  >
                    {Object.entries(INGREDIENT_DENSITIES).map(([key, { name, gramsPerCup }]) => (
                      <option key={key} value={key}>
                        {name} ({gramsPerCup} gram/cangkir)
                      </option>
                    ))}
                    <option value="custom">Kepadatan kustom</option>
                  </select>
                </div>
              </div>

              {ingredient === "custom" && (
                <div className="row mt-2">
                  <div className="col-sm-12">
                    <label htmlFor="customDensity">Kepadatan (gram/cangkir)</label>
                    <input
                      id="customDensity"
                      type="number"
                      className="form-control"
                      value={customDensity}
                      onChange={(e) => setCustomDensity(e.target.value)}
                      min="0"
                      step="1"
                      placeholder="Contoh: 125"
                      style={{ backgroundColor: "#fff3cd" }}
                    />
                  </div>
                </div>
              )}

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="grams">Jumlah dalam Gram (g)</label>
                  <input
                    id="grams"
                    type="number"
                    className="form-control"
                    value={grams}
                    onChange={handleGramsChange}
                    min="0"
                    step="1"
                    placeholder="Masukkan jumlah dalam gram"
                    style={{ backgroundColor: unit === "grams" && grams ? "#e8f5e9" : "" }}
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="cups">Jumlah dalam Cangkir (cup)</label>
                  <input
                    id="cups"
                    type="number"
                    className="form-control"
                    value={cups}
                    onChange={handleCupsChange}
                    min="0"
                    step="0.01"
                    placeholder="Masukkan jumlah dalam cangkir"
                    style={{ backgroundColor: unit === "cups" && cups ? "#e8f5e9" : "" }}
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>
                  Hapus Semua
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Keterangan</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>{result.grams.toFixed(1)} gram dari {result.ingredientName}</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            = {result.cups.toFixed(3)} cangkir
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>{result.cups.toFixed(3)} cangkir dari {result.ingredientName}</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#0f3d28" }}>
                            = {result.grams.toFixed(1)} gram
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Konversi Tambahan</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🥄 <strong>Sendok makan:</strong> {result.tablespoons} sendok makan</li>
                          <li>🥄 <strong>Sendok teh:</strong> {result.teaspoons} sendok teh</li>
                          <li>🥛 <strong>Mililiter:</strong> {result.milliliters} ml</li>
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 Info:</strong> Kepadatan yang digunakan: {result.gramsPerCup} gram/cangkir
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Perhitungan</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p><strong>Bahan:</strong> {result.ingredientName}</p>
                          <p><strong>Kepadatan bahan:</strong> {result.gramsPerCup} gram per cangkir</p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}><strong>Rumus konversi:</strong></p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            Jumlah cangkir = gram ÷ kepadatan (gram/cangkir)
                            <br />
                            {result.grams.toFixed(1)} ÷ {result.gramsPerCup} = {result.cups.toFixed(3)} cangkir
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Kalkulator Konversi Gram ke Cangkir?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Alat cerdas yang memecahkan masalah sehari-hari di setiap dapur: mengonversi takaran dari gram (berat) ke cangkir (volume). Tidak semua bahan itu sama! Satu cangkir tepung tidak sama beratnya dengan satu cangkir gula. Di sini Anda memasukkan jumlah, memilih bahan, dan hasilnya langsung muncul.
                </p>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>Cara menggunakannya:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih jenis bahan dari daftar (tepung, gula, mentega...).</li>
                  <li>Jika bahan Anda tidak ada, pilih "Kepadatan kustom" dan masukkan nilainya.</li>
                  <li>Tulis jumlah dalam gram atau cangkir - kolom kedua akan berubah otomatis.</li>
                  <li>Di bawah Anda akan melihat konversi ke sendok makan dan sendok teh.</li>
                </ol>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>Contoh:</strong> Ingin tahu berapa cangkir untuk 150 gram mentega? Pilih "Mentega", masukkan 150 gram, hasilnya: 0.66 cangkir (sekitar dua pertiga cangkir).
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Kegunaan Konverter di Dapur</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Untuk Pembuat Kue Rumahan:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Resep asing:</strong> Banyak resep menggunakan gram dan Anda tidak punya timbangan? Konversi ke cangkir.</li>
                  <li><strong>Penyesuaian resep:</strong> Ingin membuat setengah resep? Konversi jumlah dengan mudah.</li>
                  <li><strong>Berbagai jenis tepung:</strong> Tepung putih, gandum, roti - masing-masing memiliki berat berbeda per cangkir.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Memasak Sehari-hari:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Nasi dan bulgur:</strong> Ketahui berapa cangkir beras yang Anda butuhkan untuk 500 gram.</li>
                  <li><strong>Cairan:</strong> Konversi susu, air, dan minyak dengan akurat.</li>
                  <li><strong>Bahan kering:</strong> Oat, kakao, kelapa parut.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Diet:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Hitung kalori:</strong> Konversi berat makanan ke cangkir untuk aplikasi nutrisi.</li>
                  <li><strong>Resep sehat:</strong> Banyak blogger kesehatan menggunakan cangkir sebagai ukuran.</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Tabel Kepadatan Bahan Umum</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Tahukah Anda bahwa satu cangkir madu dua kali lebih berat dari satu cangkir minyak? Alasannya adalah kepadatan. Tabel ini menunjukkan berapa gram dalam satu cangkir untuk setiap bahan (cangkir = 240 ml).
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Bahan</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Gram/Cangkir</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Tepung terigu (biasa)</td><td style={{ textAlign: "center" }}>125</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Tepung roti</td><td style={{ textAlign: "center" }}>130</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Tepung gandum utuh</td><td style={{ textAlign: "center" }}>120</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gula putih</td><td style={{ textAlign: "center" }}>200</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gula merah (padat)</td><td style={{ textAlign: "center" }}>220</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gula bubuk</td><td style={{ textAlign: "center" }}>120</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Mentega</td><td style={{ textAlign: "center" }}>227</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Air / Susu</td><td style={{ textAlign: "center" }}>240</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Minyak sayur</td><td style={{ textAlign: "center" }}>218</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Madu</td><td style={{ textAlign: "center" }}>340</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Beras (mentah)</td><td style={{ textAlign: "center" }}>190</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Oat/Gandum</td><td style={{ textAlign: "center" }}>90</td></tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Tips:</strong> Bahan ringan seperti oat dan tepung memiliki massa sedikit per cangkir, sedangkan bahan berat seperti madu dan mentega memiliki berat lebih besar.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis dari Dapur Anda</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Contoh 1: Kue Vanilla</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Resep asli:</strong> 250 gram tepung, 200 gram gula, 4 telur.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Konversi:</strong></p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>250 gram tepung = 250 ÷ 125 = 2 cangkir tepung pas.</li>
                  <li>200 gram gula = 200 ÷ 200 = 1 cangkir gula.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 2: Kue Kurma</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Jumlah:</strong> 500 gram tepung, 300 gram mentega.</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Tepung: 500 ÷ 125 = 4 cangkir.</li>
                  <li>Mentega: 300 ÷ 227 = 1.32 cangkir (sekitar satu cangkir sepertiga).</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 3: Oat dengan Buah</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Jumlah:</strong> 150 gram oat, satu cangkir susu.</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Oat: 150 ÷ 90 = 1.67 cangkir (sekitar satu cangkir dua pertiga).</li>
                </ul>
                <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                  <strong>💡 Tips:</strong> Jika Anda menggandakan atau mengurangi resep, konversikan dulu kemudian kalikan.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips untuk Konversi yang Tepat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Tips Umum:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Gunakan cangkir standar:</strong> Tidak semua cangkir berukuran sama. Cangkir standar = 240 ml.</li>
                  <li><strong>Tepung penting:</strong> Jika tepung diayak atau dipadatkan, beratnya berubah. Gunakan tepung biasa yang tidak diayak untuk akurasi.</li>
                  <li><strong>Gula merah:</strong> Selalu "padat" dalam cangkir (tekan sedikit ke dalam cangkir) agar kepadatannya benar.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Kesalahan Umum:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>❌ Menggunakan ukuran yang sama untuk semua bahan:</strong> Satu cangkir gula tidak sama dengan satu cangkir tepung!</li>
                  <li><strong>❌ Mengabaikan jenis tepung:</strong> Tepung roti lebih berat dari tepung biasa.</li>
                  <li><strong>❌ Tidak membedakan antara cair dan kering:</strong> Bahan cair diukur dengan cara berbeda.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Hasil Terbaik:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Jika resep menggunakan bahan kering, lebih baik menggunakan timbangan jika tersedia. Namun konverter ini sangat akurat untuk penggunaan sehari-hari.</li>
                  <li>Coba resep sekali dengan nilai yang dikonversi, dan evaluasi hasilnya. Pasti berhasil!</li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>⚠️ Catatan:</strong> Konversi untuk bahan dalam kemasan mungkin sedikit berbeda tergantung produsen.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>Pertanyaan yang Sering Diajukan</h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa cangkir untuk 100 gram tepung?</h3>
              </div>
              <div className="info-section-body">
                100 gram tepung biasa = 0.8 cangkir. Artinya sedikit kurang dari satu cangkir. Satu cangkir penuh tepung beratnya 125 gram. Jadi 100 gram setara dengan ⅘ cangkir. Jika menggunakan tepung roti, 100 gram = 0.77 cangkir karena sedikit lebih berat.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa gram dalam satu cangkir gula putih?</h3>
              </div>
              <div className="info-section-body">
                Satu cangkir gula putih biasa = 200 gram. Jika resep memerlukan dua cangkir gula, beratnya 400 gram. Penting untuk diketahui jika Anda menggunakan timbangan. Butiran gula lebih berat dibandingkan tepung.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah konversi mentega berbeda dari bahan lainnya?</h3>
              </div>
              <div className="info-section-body">
                Ya, mentega memiliki kasus khusus. Satu cangkir mentega = 227 gram (sekitar 225 gram di kebanyakan referensi). Mudah untuk menanganinya: satu batang mentega kecil biasanya 100 gram, yaitu sekitar kurang dari setengah cangkir.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara mengonversi 500 gram tepung ke cangkir?</h3>
              </div>
              <div className="info-section-body">
                500 gram tepung ÷ 125 (kepadatan tepung) = 4 cangkir pas. Mudah, bukan? Jika tepung sudah diayak, kepadatannya berkurang sedikit (sekitar 115) sehingga Anda membutuhkan 4.3 cangkir.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apa perbedaan antara cangkir gula merah dan gula putih?</h3>
              </div>
              <div className="info-section-body">
                Gula merah mengandung molase sehingga lebih berat. Satu cangkir gula merah padat = 220 gram. Sedangkan gula putih = 200 gram. Dalam memanggang, perbedaan ini kecil tetapi berpengaruh pada jumlah yang besar.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bisakah saya mengonversi madu dengan cara yang sama seperti gula?</h3>
              </div>
              <div className="info-section-body">
                Sama sekali tidak! Kepadatan madu sangat tinggi. Satu cangkir madu = 340 gram, sedangkan gula 200 gram. Jika Anda mengganti satu cangkir gula dengan satu cangkir madu dalam resep, hasilnya akan berbeda jauh. Selalu gunakan konverter yang sesuai.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa cangkir untuk 250 gram beras?</h3>
              </div>
              <div className="info-section-body">
                250 gram beras mentah = sekitar 1.3 cangkir (satu cangkir sepertiga). Kepadatan beras adalah 190 gram/cangkir. Jika beras dimasak, Anda akan melihat volumenya meningkat 3 kali lipat!
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Timbangan saya tidak tersedia, apakah konverter ini akurat?</h3>
              </div>
              <div className="info-section-body">
                Konverter ini sangat akurat jika Anda menggunakan cangkir standar (240 ml). Penelitian menunjukkan bahwa cangkir rumah tangga sangat bervariasi, jadi cobalah membeli cangkir ukur murah dari supermarket. Dengan latihan, Anda akan bisa mengukur takaran dengan perkiraan!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GramsToCupsCalculator;